import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, Target, AlertCircle, Plus, Trash2, Edit, User } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { localToday } from '@/lib/dateRange';

export default function PerformanceReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const { t } = useLanguage();
  const [goals, setGoals] = useState([]);
  const [disciplinary, setDisciplinary] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [showDisciplinaryDialog, setShowDisciplinaryDialog] = useState(false);
  const [reviewCycles, setReviewCycles] = useState([]);
  const [showCycleDialog, setShowCycleDialog] = useState(false);
  const [cycleForm, setCycleForm] = useState({ name: '', cycle_type: 'annual', start_date: '', end_date: '' });
  const [cycleProgress, setCycleProgress] = useState(null);
  const [reviewForm, setReviewForm] = useState({ employee_id: '', period: '', review_type: 'annual', overall_rating: 3, strengths: '', improvements: '', goals_for_next_period: '', kpis: [] });
  const [goalForm, setGoalForm] = useState({ employee_id: '', title: '', description: '', category: 'performance', priority: 'medium', target_date: '', progress: 0 });
  const [discForm, setDiscForm] = useState({ employee_id: '', action_type: 'verbal_warning', reason: '', description: '', severity: 'low', date: localToday() });

  const fetchData = async () => {
    try {
      const [revRes, goalRes, discRes, empRes, cycRes] = await Promise.all([
        api.get('/performance-reviews'),
        api.get('/goals'),
        api.get('/disciplinary'),
        api.get('/employees'),
        api.get('/review-cycles').catch(() => ({ data: [] })),
      ]);
      setReviews(revRes.data);
      setGoals(goalRes.data);
      setDisciplinary(discRes.data);
      setEmployees(empRes.data.filter(e => e.status === 'active' || e.active));
      setReviewCycles(cycRes.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.employee_id) { toast.error('Select an employee'); return; }
    try {
      await api.post('/performance-reviews', { ...reviewForm, status: 'completed' });
      toast.success('Review created');
      setShowReviewDialog(false);
      setReviewForm({ employee_id: '', period: '', review_type: 'annual', overall_rating: 3, strengths: '', improvements: '', goals_for_next_period: '', kpis: [] });
      fetchData();
    } catch (err) {
      toast.error('Failed to create review');
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    if (!goalForm.employee_id || !goalForm.title) { toast.error('Employee and title required'); return; }
    try {
      await api.post('/goals', goalForm);
      toast.success('Goal created');
      setShowGoalDialog(false);
      setGoalForm({ employee_id: '', title: '', description: '', category: 'performance', priority: 'medium', target_date: '', progress: 0 });
      fetchData();
    } catch (err) {
      toast.error('Failed to create goal');
    }
  };

  const handleCreateDisciplinary = async (e) => {
    e.preventDefault();
    if (!discForm.employee_id) { toast.error('Select an employee'); return; }
    try {
      await api.post('/disciplinary', discForm);
      toast.success('Disciplinary action recorded');
      setShowDisciplinaryDialog(false);
      setDiscForm({ employee_id: '', action_type: 'verbal_warning', reason: '', description: '', severity: 'low', date: localToday() });
      fetchData();
    } catch (err) {
      toast.error('Failed to create');
    }
  };

  const handleDeleteReview = async (id) => { if (window.confirm('Delete?')) { await api.delete(`/performance-reviews/${id}`); fetchData(); } };
  const handleDeleteGoal = async (id) => { if (window.confirm('Delete?')) { await api.delete(`/goals/${id}`); fetchData(); } };
  const handleDeleteDisc = async (id) => { if (window.confirm('Delete?')) { await api.delete(`/disciplinary/${id}`); fetchData(); } };

  const handleUpdateGoalProgress = async (goalId, progress) => {
    try {
      await api.put(`/goals/${goalId}`, { progress, status: progress >= 100 ? 'completed' : 'in_progress' });
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const ratingStars = (rating) => Array.from({ length: 5 }, (_, i) => (
    <Star key={i} size={14} className={i < rating ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'} />
  ));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="performance-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-200/50">

                <Star size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">{t('performance_title') || 'Performance & Reviews'}</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Employee reviews and goal tracking</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm mt-1">{t('performance_subtitle') || 'Track reviews, goals, and disciplinary actions.'}</p>
          </div>
          <PageTour storageKey="tour_performance" steps={[
            { target: '[data-testid="add-review-btn"]', title: 'New Review', content: 'Create performance reviews for employees with ratings and feedback.' },
            { target: '[data-testid="add-goal-btn"]', title: 'Goals & OKRs', content: 'Set measurable goals and track progress with key results.' },
            { target: '[data-testid="perf-tabs"]', title: 'Sections', content: 'Switch between reviews, review cycles, goals, and disciplinary actions.' },
          ]} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="stat-reviews"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-info/10 rounded-lg"><Star size={18} className="text-info" /></div><div><div className="text-2xl font-bold">{reviews.length}</div><div className="text-xs text-muted-foreground">Reviews</div></div></div></CardContent></Card>
          <Card data-testid="stat-goals"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-success/10 rounded-lg"><Target size={18} className="text-success" /></div><div><div className="text-2xl font-bold">{goals.length}</div><div className="text-xs text-muted-foreground">Goals</div></div></div></CardContent></Card>
          <Card data-testid="stat-goals-done"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-warning/10 rounded-lg"><Target size={18} className="text-warning" /></div><div><div className="text-2xl font-bold">{goals.filter(g => g.status === 'completed').length}</div><div className="text-xs text-muted-foreground">Goals Completed</div></div></div></CardContent></Card>
          <Card data-testid="stat-disc"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-error/10 rounded-lg"><AlertCircle size={18} className="text-error" /></div><div><div className="text-2xl font-bold">{disciplinary.length}</div><div className="text-xs text-muted-foreground">Disciplinary Actions</div></div></div></CardContent></Card>
        </div>

        <Tabs defaultValue="reviews" className="space-y-4">
          <TabsList data-testid="perf-tabs-2">
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
            <TabsTrigger value="cycles">Review Cycles</TabsTrigger>
            <TabsTrigger value="goals">Goals & KPIs</TabsTrigger>
            <TabsTrigger value="disciplinary">Disciplinary</TabsTrigger>
          </TabsList>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-base">Performance Reviews</CardTitle><Button size="sm" onClick={() => setShowReviewDialog(true)} data-testid="add-review-btn-2"><Plus size={14} className="mr-1" /> New Review</Button></div></CardHeader>
              <CardContent>
                {reviews.length === 0 ? <div className="text-center py-8 text-muted-foreground text-sm">No reviews yet.</div> : (
                  <div className="space-y-3">
                    {reviews.map(r => (
                      <div key={r.id} className="border rounded-lg p-4 hover:bg-muted/50" data-testid={`review-${r.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className="p-1.5 bg-muted rounded-full"><User size={14} /></div>
                            <div><div className="font-medium text-sm">{r.employee_name}</div><div className="text-xs text-muted-foreground">{r.position} - {r.period}</div></div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex">{ratingStars(r.overall_rating)}</div>
                            <Badge variant={r.review_type === 'annual' ? 'default' : 'secondary'} className="text-xs capitalize">{r.review_type}</Badge>
                            <Button size="sm" variant="ghost" className="text-error" onClick={() => handleDeleteReview(r.id)}><Trash2 size={14} /></Button>
                          </div>
                        </div>
                        {r.strengths && <div className="text-xs text-muted-foreground mt-1"><span className="font-medium">Strengths:</span> {r.strengths}</div>}
                        {r.improvements && <div className="text-xs text-muted-foreground"><span className="font-medium">Improvements:</span> {r.improvements}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Review Cycles Tab */}
          <TabsContent value="cycles">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Review Cycles</CardTitle>
                  <Button size="sm" onClick={() => { setCycleForm({ name: '', cycle_type: 'annual', start_date: '', end_date: '' }); setShowCycleDialog(true); }} data-testid="add-cycle-btn"><Plus size={14} className="mr-1" /> New Cycle</Button>
                </div>
              </CardHeader>
              <CardContent>
                {reviewCycles.length === 0 ? <div className="text-center py-8 text-muted-foreground text-sm">No review cycles yet. Create one to auto-generate reviews for all employees.</div> : (
                  <div className="space-y-3">
                    {reviewCycles.map(c => (
                      <div key={c.id} className="border rounded-lg p-4 hover:bg-muted/50" data-testid={`cycle-${c.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-sm flex items-center gap-2">
                              {c.name}
                              <Badge variant={c.status === 'active' ? 'default' : 'secondary'} className="text-[10px] capitalize">{c.status}</Badge>
                              <Badge variant="outline" className="text-[10px] capitalize">{c.cycle_type}</Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {c.start_date} to {c.end_date} | {c.total_reviews || 0} reviews ({c.completed_reviews || 0} completed)
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
                              try {
                                const res = await api.get(`/review-cycles/${c.id}/progress`);
                                setCycleProgress(res.data);
                              } catch { toast.error('Failed to load'); }
                            }} data-testid={`view-progress-${c.id}`}>Progress</Button>
                            <Button size="sm" variant="ghost" className="text-error" onClick={async () => {
                              if (!confirm('Delete this cycle and all its reviews?')) return;
                              await api.delete(`/review-cycles/${c.id}`);
                              toast.success('Deleted');
                              fetchData();
                            }}><Trash2 size={14} /></Button>
                          </div>
                        </div>
                        {c.total_reviews > 0 && (
                          <div className="mt-2 w-full bg-muted rounded-full h-2">
                            <div className="bg-success h-2 rounded-full transition-all" style={{ width: `${Math.min(100, ((c.completed_reviews || 0) / c.total_reviews) * 100)}%` }} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {cycleProgress && (
                  <div className="mt-4 p-4 border rounded-lg bg-muted/50 space-y-3" data-testid="cycle-progress">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-semibold">Cycle Progress</h3>
                      <Button size="sm" variant="ghost" onClick={() => setCycleProgress(null)} className="h-6 text-xs">Close</Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                      <div className="p-2 rounded-lg bg-white border"><div className="text-lg font-bold">{cycleProgress.total_reviews}</div><div className="text-[10px] text-muted-foreground">Total</div></div>
                      <div className="p-2 rounded-lg bg-success/10 border border-success/30"><div className="text-lg font-bold text-success">{cycleProgress.completed}</div><div className="text-[10px] text-muted-foreground">Completed</div></div>
                      <div className="p-2 rounded-lg bg-warning/10 border border-warning/30"><div className="text-lg font-bold text-warning">{cycleProgress.in_progress}</div><div className="text-[10px] text-muted-foreground">In Progress</div></div>
                      <div className="p-2 rounded-lg bg-muted/50 border"><div className="text-lg font-bold">{cycleProgress.pending}</div><div className="text-[10px] text-muted-foreground">Pending</div></div>
                    </div>
                    {cycleProgress.avg_rating > 0 && <div className="text-xs text-center">Avg Rating: <span className="font-bold">{cycleProgress.avg_rating}/5</span> | Completion: <span className="font-bold">{cycleProgress.completion_pct}%</span></div>}
                    {cycleProgress.by_department?.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium">By Department:</p>
                        {cycleProgress.by_department.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <span className="w-24 truncate">{d.department}</span>
                            <div className="flex-1 bg-muted rounded-full h-1.5"><div className="bg-info h-1.5 rounded-full" style={{ width: `${d.completion_pct}%` }} /></div>
                            <span className="w-16 text-right">{d.completed}/{d.total}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Goals Tab */}
          <TabsContent value="goals">
            <Card>
              <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-base">Goals & OKRs</CardTitle><Button size="sm" onClick={() => setShowGoalDialog(true)} data-testid="add-goal-btn-2"><Plus size={14} className="mr-1" /> New Goal</Button></div></CardHeader>
              <CardContent>
                {goals.length === 0 ? <div className="text-center py-8 text-muted-foreground text-sm">No goals set yet.</div> : (
                  <div className="space-y-3">
                    {goals.map(g => (
                      <div key={g.id} className="border rounded-lg p-4 hover:bg-muted/50" data-testid={`goal-${g.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div><div className="font-medium text-sm">{g.title}</div><div className="text-xs text-muted-foreground">{g.employee_name} - Due: {g.target_date || 'No date'}</div></div>
                          <div className="flex items-center gap-2">
                            <Badge variant={g.priority === 'high' ? 'destructive' : g.priority === 'medium' ? 'secondary' : 'outline'} className="text-xs capitalize">{g.priority}</Badge>
                            <Badge variant={g.status === 'completed' ? 'default' : 'secondary'} className="text-xs capitalize">{g.status}</Badge>
                            <Button size="sm" variant="ghost" className="text-error" onClick={() => handleDeleteGoal(g.id)}><Trash2 size={14} /></Button>
                          </div>
                        </div>
                        {g.description && <div className="text-xs text-muted-foreground mb-2">{g.description}</div>}
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-muted rounded-full h-2">
                            <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${g.progress}%` }} />
                          </div>
                          <span className="text-xs font-medium">{g.progress}%</span>
                          <div className="flex gap-1">
                            {[25, 50, 75, 100].map(p => (
                              <Button key={p} size="sm" variant="ghost" className="h-6 w-8 text-[10px]" onClick={() => handleUpdateGoalProgress(g.id, p)}>{p}%</Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Disciplinary Tab */}
          <TabsContent value="disciplinary">
            <Card>
              <CardHeader><div className="flex items-center justify-between"><CardTitle className="text-base">Disciplinary Actions & Warnings</CardTitle><Button size="sm" onClick={() => setShowDisciplinaryDialog(true)} data-testid="add-disc-btn"><Plus size={14} className="mr-1" /> Record Action</Button></div></CardHeader>
              <CardContent>
                {disciplinary.length === 0 ? <div className="text-center py-8 text-muted-foreground text-sm">No disciplinary actions recorded.</div> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="disc-table">
                      <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="pb-2 pr-4">Date</th><th className="pb-2 pr-4">Employee</th><th className="pb-2 pr-4">Type</th><th className="pb-2 pr-4">Reason</th><th className="pb-2 pr-4">Severity</th><th className="pb-2">Actions</th></tr></thead>
                      <tbody>
                        {disciplinary.map(d => (
                          <tr key={d.id} className="border-b last:border-0 hover:bg-muted/50" data-testid={`disc-row-${d.id}`}>
                            <td className="py-2.5 pr-4 text-xs">{d.date}</td>
                            <td className="py-2.5 pr-4 font-medium">{d.employee_name}</td>
                            <td className="py-2.5 pr-4"><Badge variant="outline" className="text-[10px] capitalize">{d.action_type?.replace(/_/g, ' ')}</Badge></td>
                            <td className="py-2.5 pr-4 text-xs max-w-xs truncate">{d.reason}</td>
                            <td className="py-2.5 pr-4"><Badge variant={d.severity === 'high' ? 'destructive' : d.severity === 'medium' ? 'secondary' : 'outline'} className="text-xs">{d.severity}</Badge></td>
                            <td className="py-2.5"><Button size="sm" variant="ghost" className="text-error" onClick={() => handleDeleteDisc(d.id)}><Trash2 size={14} /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Review Dialog */}
        <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
          <DialogContent className="max-w-lg" data-testid="review-dialog">
            <DialogHeader><DialogTitle>New Performance Review</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateReview} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Employee *</Label>
                  <Select value={reviewForm.employee_id || 'none'} onValueChange={(v) => setReviewForm({ ...reviewForm, employee_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="review-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Period</Label><Input value={reviewForm.period} onChange={(e) => setReviewForm({ ...reviewForm, period: e.target.value })} placeholder="e.g. Q1 2026" data-testid="review-period" /></div>
                <div><Label>Review Type</Label>
                  <Select value={reviewForm.review_type} onValueChange={(v) => setReviewForm({ ...reviewForm, review_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="annual">Annual</SelectItem><SelectItem value="quarterly">Quarterly</SelectItem><SelectItem value="probation">Probation</SelectItem><SelectItem value="special">Special</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" value={reviewForm.overall_rating} onChange={(e) => setReviewForm({ ...reviewForm, overall_rating: parseInt(e.target.value) || 1 })} data-testid="review-rating" /></div>
              </div>
              <div><Label>Strengths</Label><Textarea value={reviewForm.strengths} onChange={(e) => setReviewForm({ ...reviewForm, strengths: e.target.value })} rows={2} /></div>
              <div><Label>Areas for Improvement</Label><Textarea value={reviewForm.improvements} onChange={(e) => setReviewForm({ ...reviewForm, improvements: e.target.value })} rows={2} /></div>
              <div><Label>Goals for Next Period</Label><Textarea value={reviewForm.goals_for_next_period} onChange={(e) => setReviewForm({ ...reviewForm, goals_for_next_period: e.target.value })} rows={2} /></div>
              <Button type="submit" className="w-full" data-testid="review-submit">Submit Review</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Goal Dialog */}
        <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
          <DialogContent data-testid="goal-dialog">
            <DialogHeader><DialogTitle>New Goal / OKR</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateGoal} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Employee *</Label>
                  <Select value={goalForm.employee_id || 'none'} onValueChange={(v) => setGoalForm({ ...goalForm, employee_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="goal-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Priority</Label>
                  <Select value={goalForm.priority} onValueChange={(v) => setGoalForm({ ...goalForm, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Title *</Label><Input value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} data-testid="goal-title" required /></div>
              <div><Label>Description</Label><Textarea value={goalForm.description} onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })} rows={2} /></div>
              <div><Label>Target Date</Label><Input type="date" value={goalForm.target_date} onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })} /></div>
              <Button type="submit" className="w-full" data-testid="goal-submit">Create Goal</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Disciplinary Dialog */}
        <Dialog open={showDisciplinaryDialog} onOpenChange={setShowDisciplinaryDialog}>
          <DialogContent data-testid="disc-dialog">
            <DialogHeader><DialogTitle>Record Disciplinary Action</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateDisciplinary} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Employee *</Label>
                  <Select value={discForm.employee_id || 'none'} onValueChange={(v) => setDiscForm({ ...discForm, employee_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="disc-emp"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Action Type</Label>
                  <Select value={discForm.action_type} onValueChange={(v) => setDiscForm({ ...discForm, action_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="verbal_warning">Verbal Warning</SelectItem><SelectItem value="written_warning">Written Warning</SelectItem><SelectItem value="final_warning">Final Warning</SelectItem><SelectItem value="suspension">Suspension</SelectItem><SelectItem value="termination">Termination</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Severity</Label>
                  <Select value={discForm.severity} onValueChange={(v) => setDiscForm({ ...discForm, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Date</Label><Input type="date" value={discForm.date} onChange={(e) => setDiscForm({ ...discForm, date: e.target.value })} /></div>
              </div>
              <div><Label>Reason *</Label><Input value={discForm.reason} onChange={(e) => setDiscForm({ ...discForm, reason: e.target.value })} data-testid="disc-reason" required /></div>
              <div><Label>Description</Label><Textarea value={discForm.description} onChange={(e) => setDiscForm({ ...discForm, description: e.target.value })} rows={2} /></div>
              <Button type="submit" className="w-full" data-testid="disc-submit">Record Action</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Review Cycle Dialog */}
        <Dialog open={showCycleDialog} onOpenChange={setShowCycleDialog}>
          <DialogContent className="max-w-md" data-testid="cycle-dialog">
            <DialogHeader><DialogTitle>New Review Cycle</DialogTitle></DialogHeader>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!cycleForm.name) { toast.error('Cycle name required'); return; }
              try {
                const res = await api.post('/review-cycles', cycleForm);
                toast.success(`Review cycle created with ${res.data.total_reviews} reviews`);
                setShowCycleDialog(false);
                fetchData();
              } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
            }} className="space-y-3">
              <div><Label>Cycle Name *</Label><Input value={cycleForm.name} onChange={e => setCycleForm({...cycleForm, name: e.target.value})} placeholder="Q1 2026 Performance Review" required data-testid="cycle-name" /></div>
              <div><Label>Type</Label>
                <Select value={cycleForm.cycle_type} onValueChange={v => setCycleForm({...cycleForm, cycle_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="midyear">Mid-Year</SelectItem>
                    <SelectItem value="probation">Probation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start Date</Label><Input type="date" value={cycleForm.start_date} onChange={e => setCycleForm({...cycleForm, start_date: e.target.value})} /></div>
                <div><Label>End Date</Label><Input type="date" value={cycleForm.end_date} onChange={e => setCycleForm({...cycleForm, end_date: e.target.value})} /></div>
              </div>
              <p className="text-xs text-muted-foreground">This will auto-generate review entries for all active employees.</p>
              <Button type="submit" className="w-full" data-testid="cycle-submit">Create Cycle</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
